import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ApiConstants } from "../../main/constants/ApiConstants";
import PublicLanguageSwitch from "../hotel/public/PublicLanguageSwitch";
import { buildEntityFileUrl } from "../../main/utils/fileUrl";
import useGetUser from "../../main/hooks/useGetUser";
import axiosInstance from "../../main/initializers/axios";
import "../../main/theme/theme.css";
import { AppTheme, getStoredMainLandingTheme, MAIN_LANDING_THEMES } from "../../main/theme/publicTheme";
import AuthManager from "../../main/utils/authManager";
import { invalidateUser, setUser } from "../../main/store/stores/user/user.store";
import { invalidateMenu } from "../../main/store/stores/user/menu.store";
import { invalidatePermissions } from "../../main/store/stores/user/permission.store";
import GeneralModal from "../../main/components/modal/general/GeneralModal";
import "./UserMonitor.css";
import "../hotel/public/PublicExperience.css";
import "./PublicUserMonitorPage.css";

type MonitorReservation = {
  id: number;
  hotelId: number;
  hotelName: string;
  roomNumber: string;
  guestName: string;
  checkInDate: string;
  checkOutDate: string;
  lockedPriceTotal: number;
  balanceDue: number;
  status: number;
  created: string;
};

const normalizeMoney = (...values: Array<number | string | null | undefined>) => {
  for (const value of values) {
    const amount = Number(value ?? 0);
    if (Number.isFinite(amount) && amount > 0) {
      return amount;
    }
  }

  return 0;
};

const isActiveReservation = (status: number) => status === 1 || status === 2;

type MonitorBookingRequest = {
  id: number;
  hotelId: number;
  hotelName: string;
  roomTypeName: string;
  roomNumber: string;
  checkInDate: string;
  checkOutDate: string;
  created: string;
  status: number;
  specialRequests: string;
};

type MonitorActivity = {
  type: string;
  title: string;
  description: string;
  occurredAt: string;
  hotelId?: number | null;
  hotelName?: string;
  referenceId?: number | null;
  status?: number | null;
};

type MonitorResponse = {
  user: {
    id: number;
    companyId: number | null;
    companyName?: string;
    companyTimeZone?: string;
    userName: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    avatar?: string;
    role?: { name?: string; code?: string };
  };
  reservations: MonitorReservation[];
  bookingRequests: MonitorBookingRequest[];
  activities: MonitorActivity[];
};

const THEME_LABELS: Record<AppTheme, string> = {
  light: "Light",
  sky: "Sky",
  dark: "Dark",
  ocean: "Ocean",
  pearl: "Pearl",
  forest: "Forest",
};

const PublicUserMonitor = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useGetUser();
  const [theme, setTheme] = useState<AppTheme>(getStoredMainLandingTheme);
  const bookingUser = currentUser;
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const themeDropdownRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<MonitorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [cancellingRequestId, setCancellingRequestId] = useState<number | null>(null);
  const [cancelRequestItem, setCancelRequestItem] = useState<MonitorBookingRequest | null>(null);
  const [profilePhone, setProfilePhone] = useState(bookingUser?.phone ?? "");
  const [profileCurrentPassword, setProfileCurrentPassword] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");
  const [passwordCurrent, setPasswordCurrent] = useState("");
  const [passwordNext, setPasswordNext] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    if (!isThemeOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!themeDropdownRef.current?.contains(event.target as Node)) {
        setIsThemeOpen(false);
      }
      if (!mobileMenuRef.current?.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsThemeOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isThemeOpen]);

  const locale = useMemo(() => {
    const lang = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
    if (lang === "al") return "sq-AL";
    if (lang === "it") return "it-IT";
    return "en-US";
  }, [i18n.language, i18n.resolvedLanguage]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "ALL",
        maximumFractionDigits: 2,
      }),
    [locale]
  );

  const bookingRequestStatusLabel = (status: number) => {
    switch (status) {
      case 1:
        return t("bookingMonitor.requestPending");
      case 2:
        return t("bookingMonitor.requestApproved");
      case 3:
        return t("bookingMonitor.requestRejected");
      case 4:
        return t("bookingMonitor.requestCancelled");
      case 5:
        return t("bookingMonitor.requestExpired");
      default:
        return `#${status}`;
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (value?: string) => {
    if (!value) return "-";
    return new Date(value).toLocaleString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!bookingUser?.email) {
        setData(null);
        setError("");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const response = await axiosInstance.get(ApiConstants.publicUser.monitor);

        if (mounted) {
          setData(response.data);
        }
      } catch (err: any) {
        if (mounted) {
          setError(err?.response?.data?.message || err?.response?.data || t("bookingMonitor.loadFailed"));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, [bookingUser?.email, t]);

  const monitorUser = useMemo(() => {
    const rawUser = (data as any)?.user ?? (data as any)?.User ?? bookingUser;

    if (!rawUser) {
      return null;
    }

    return {
      id: Number(rawUser.id ?? rawUser.Id ?? 0),
      companyId: rawUser.companyId ?? rawUser.CompanyId ?? null,
      companyName: rawUser.companyName ?? rawUser.CompanyName ?? "",
      companyTimeZone: rawUser.companyTimeZone ?? rawUser.CompanyTimeZone ?? "",
      userName: rawUser.userName ?? rawUser.UserName ?? "",
      email: rawUser.email ?? rawUser.Email ?? "",
      firstName: rawUser.firstName ?? rawUser.FirstName ?? "",
      lastName: rawUser.lastName ?? rawUser.LastName ?? "",
      phone: rawUser.phone ?? rawUser.Phone ?? "",
      avatar: rawUser.avatar ?? rawUser.Avatar ?? "",
      role: rawUser.role ?? rawUser.Role,
    };
  }, [bookingUser, data]);

  const reservations = useMemo(() => {
    const rawReservations = ((data as any)?.reservations ?? (data as any)?.Reservations ?? []) as any[];

    return rawReservations
      .map((item) => ({
        id: Number(item.id ?? item.Id ?? 0),
        hotelId: Number(item.hotelId ?? item.HotelId ?? 0),
        hotelName: item.hotelName ?? item.HotelName ?? "",
        roomNumber: item.roomNumber ?? item.RoomNumber ?? "",
        guestName: item.guestName ?? item.GuestName ?? "",
        checkInDate: item.checkInDate ?? item.CheckInDate ?? "",
        checkOutDate: item.checkOutDate ?? item.CheckOutDate ?? "",
        lockedPriceTotal: normalizeMoney(item.lockedPriceTotal, item.LockedPriceTotal, item.totalPrice, item.TotalPrice),
        balanceDue: normalizeMoney(item.balanceDue, item.BalanceDue),
        status: Number(item.status ?? item.Status ?? 0),
        created: item.created ?? item.Created ?? "",
      }))
      .sort((a, b) => {
        const left = new Date(b.created || b.checkInDate || 0).getTime();
        const right = new Date(a.created || a.checkInDate || 0).getTime();
        return left - right;
      });
  }, [data]);

  const activeReservations = useMemo(
    () => reservations.filter((item) => isActiveReservation(item.status)),
    [reservations]
  );

  const bookingRequests = useMemo(() => {
    const rawBookingRequests = ((data as any)?.bookingRequests ?? (data as any)?.BookingRequests ?? []) as any[];

    return rawBookingRequests
      .map((item) => ({
        id: Number(item.id ?? item.Id ?? 0),
        hotelId: Number(item.hotelId ?? item.HotelId ?? 0),
        hotelName: item.hotelName ?? item.HotelName ?? "",
        roomTypeName: item.roomTypeName ?? item.RoomTypeName ?? "",
        roomNumber: item.roomNumber ?? item.RoomNumber ?? "",
        checkInDate: item.checkInDate ?? item.CheckInDate ?? "",
        checkOutDate: item.checkOutDate ?? item.CheckOutDate ?? "",
        created: item.created ?? item.Created ?? "",
        status: Number(item.status ?? item.Status ?? 0),
        specialRequests: item.specialRequests ?? item.SpecialRequests ?? "",
      }))
      .sort((a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime());
  }, [data]);

  const awaitingCheckInReservations = useMemo(
    () =>
      reservations.filter((item) => {
        const status = Number(item.status ?? 0);
        return status === 1 || status === 2;
      }),
    [reservations]
  );

  const summary = useMemo(() => {
    return {
      reservations: reservations.length,
      bookingRequests: bookingRequests.length,
      activeStays: activeReservations.length,
      totalSpent: activeReservations.reduce((sum, item) => sum + normalizeMoney(item.lockedPriceTotal, item.balanceDue), 0),
    };
  }, [activeReservations, bookingRequests.length, reservations.length]);

  const user = monitorUser ?? bookingUser;
  const displayName =
    user?.firstName || user?.lastName
      ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
      : user?.userName || t("bookingMonitor.publicUser");

  useEffect(() => {
    setProfilePhone(user?.phone ?? "");
  }, [user?.phone]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [user?.id, user?.avatar]);

  const visibleBookingRequests = useMemo(
    () => (showAllRequests ? bookingRequests : bookingRequests.slice(0, 3)),
    [bookingRequests, showAllRequests]
  );
  const hasMoreRequests = bookingRequests.length > 3;

  useEffect(() => {
    setShowAllRequests(false);
  }, [bookingUser?.email, bookingRequests.length]);

  const syncStoredUser = (nextValues: Partial<NonNullable<typeof bookingUser>>) => {
    const baseUser = monitorUser ?? bookingUser;
    if (!baseUser?.email) {
      return;
    }

    const nextUser = {
      id: Number(baseUser.id ?? 0),
      email: baseUser.email,
      userName: baseUser.userName ?? "",
      firstName: baseUser.firstName ?? "",
      lastName: baseUser.lastName ?? "",
      phone: nextValues.phone ?? baseUser.phone ?? "",
      avatar: nextValues.avatar ?? baseUser.avatar ?? "",
      pageRedirect: 1,
    };

    dispatch(setUser(nextUser as any));
    setData((current) =>
      current
        ? {
            ...current,
            user: {
              ...current.user,
              phone: nextUser.phone,
              avatar: nextUser.avatar,
            },
          }
        : current
    );
  };

  const handleProfileSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!user?.email) {
      return;
    }

    if (!profileCurrentPassword.trim()) {
      setProfileError(t("bookingMonitor.currentPasswordRequired"));
      setProfileMessage("");
      return;
    }

    try {
      setProfileSaving(true);
      setProfileError("");
      setProfileMessage("");

      const response = await axiosInstance.put(ApiConstants.publicUser.profile, {
        email: user.email,
        phone: profilePhone.trim(),
        currentPassword: profileCurrentPassword.trim(),
      });

      const nextPhone = response?.data?.phone ?? response?.data?.Phone ?? profilePhone.trim();
      setProfilePhone(nextPhone);
      setProfileCurrentPassword("");
      syncStoredUser({ phone: nextPhone });
      setProfileMessage(t("bookingMonitor.profileUpdated"));
    } catch (err: any) {
      setProfileError(err?.response?.data?.message || err?.response?.data || t("bookingMonitor.profileUpdateFailed"));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!user?.email) {
      return;
    }

    if (!passwordCurrent.trim()) {
      setPasswordError(t("bookingMonitor.currentPasswordRequired"));
      setPasswordMessage("");
      return;
    }

    if (!passwordNext.trim()) {
      setPasswordError(t("bookingMonitor.newPasswordRequired"));
      setPasswordMessage("");
      return;
    }

    if (passwordNext.trim().length < 6) {
      setPasswordError(t("publicBooking.passwordShort"));
      setPasswordMessage("");
      return;
    }

    if (passwordNext !== passwordConfirm) {
      setPasswordError(t("publicBooking.passwordMismatch"));
      setPasswordMessage("");
      return;
    }

    try {
      setPasswordSaving(true);
      setPasswordError("");
      setPasswordMessage("");

      await axiosInstance.put(ApiConstants.publicUser.changePassword, {
        email: user.email,
        currentPassword: passwordCurrent.trim(),
        newPassword: passwordNext.trim(),
        confirmPassword: passwordConfirm.trim(),
      });

      setPasswordCurrent("");
      setPasswordNext("");
      setPasswordConfirm("");
      setPasswordMessage(t("bookingMonitor.passwordChanged"));
    } catch (err: any) {
      setPasswordError(err?.response?.data?.message || err?.response?.data || t("bookingMonitor.passwordChangeFailed"));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogout = () => {
    AuthManager.logout();
    dispatch(invalidateUser());
    dispatch(invalidateMenu());
    dispatch(invalidatePermissions());
    setData(null);
    setError("");
    navigate("/login", { replace: true });
  };

  const cancelBookingRequest = async (item?: MonitorBookingRequest | null) => {
    if (!item || item.status !== 1) {
      return;
    }

    try {
      setCancellingRequestId(item.id);
      await axiosInstance.put(ApiConstants.publicUser.cancelBookingRequest, {
        id: item.id,
        guestEmail: user?.email || bookingUser?.email || "",
      });

      setData((current) =>
        current
          ? {
              ...current,
              bookingRequests: (current.bookingRequests || []).map((request) =>
                Number(request.id) === Number(item.id)
                  ? {
                      ...request,
                      status: 4,
                    }
                  : request,
              ),
            }
          : current,
      );

      toast.success(t("bookingMonitor.requestCancelled", { defaultValue: "Booking request cancelled." }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data || t("bookingMonitor.cancelRequestFailed", { defaultValue: "Failed to cancel booking request." }));
    } finally {
      setCancellingRequestId(null);
    }
  };

  const openCancelRequestModal = (item: MonitorBookingRequest) => {
    setCancelRequestItem(item);
  };

  const closeCancelRequestModal = () => {
    if (cancellingRequestId) {
      return;
    }

    setCancelRequestItem(null);
  };

  const submitCancelRequest = async () => {
    if (!cancelRequestItem) {
      return;
    }

    await cancelBookingRequest(cancelRequestItem);
    setCancelRequestItem(null);
  };

  return (
    <div className={`um-page bcShell bcTheme-${theme}`}>
      <header className="um-top-nav">
        <div className="um-nav-container">
          <div className="um-nav-left">
            <button
              type="button"
              className="um-back-btn"
              onClick={() => {
                if (window.history.length > 1) {
                  window.history.back();
                  return;
                }

                window.location.href = "/booking";
              }}
            >
              <KeyboardBackspaceOutlinedIcon fontSize="inherit" />
              {t("generic.back")}
            </button>
            <button type="button" className="um-brand" onClick={() => navigate("/booking")}>
              <span className="um-brand-icon">
                <ApartmentOutlinedIcon fontSize="inherit" />
              </span>
              <span className="um-brand-text">{t("bookingMonitor.title")}</span>
            </button>
          </div>

          <div className={`um-nav-right ${isMobileMenuOpen ? "is-open" : ""}`} ref={mobileMenuRef}>
            <button
              type="button"
              className="um-mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen((current) => !current)}
              aria-haspopup="menu"
              aria-expanded={isMobileMenuOpen}
              aria-label={t("generic.menu", { defaultValue: "Menu" })}
            >
              <MenuRoundedIcon fontSize="inherit" />
            </button>
            <div className={`um-nav-actions ${isMobileMenuOpen ? "is-open" : ""}`} role="menu">
              <button
                type="button"
                className="um-header-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate("/booking");
                }}
              >
                <KeyboardBackspaceOutlinedIcon fontSize="inherit" />
                {t("bookingMonitor.goBooking")}
              </button>
              <button
                type="button"
                className="um-header-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  window.location.reload();
                }}
              >
                <RefreshOutlinedIcon fontSize="inherit" />
                Refresh
              </button>
              <button
                type="button"
                className="um-header-btn um-header-btn--profile"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsProfileModalOpen(true);
                }}
              >
                <span className="um-profile-chip" aria-hidden="true">
                  {(displayName || "U").slice(0, 1).toUpperCase()}
                </span>
                <AccountCircleOutlinedIcon fontSize="inherit" />
                {t("bookingMonitor.profile")}
              </button>
              <div className="theme-dropdown um-theme-dropdown" ref={themeDropdownRef}>
                <button
                  type="button"
                  className="theme-dropdown-trigger um-theme-trigger"
                  onClick={() => setIsThemeOpen((current) => !current)}
                  aria-haspopup="menu"
                  aria-expanded={isThemeOpen}
                  aria-label={t("generic.theme", { defaultValue: "Theme" })}
                >
                  <PaletteOutlinedIcon fontSize="inherit" />
                  <span>{THEME_LABELS[theme]}</span>
                  <KeyboardArrowDownRoundedIcon fontSize="inherit" />
                </button>

                {isThemeOpen ? (
                  <div className="theme-dropdown-menu um-theme-menu" role="menu">
                    {MAIN_LANDING_THEMES.map((themeOption) => (
                      <button
                        key={themeOption}
                        type="button"
                        className={`theme-dropdown-item ${theme === themeOption ? "active" : ""}`}
                        onClick={() => {
                          setTheme(themeOption);
                          setIsThemeOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        role="menuitemradio"
                        aria-checked={theme === themeOption}
                      >
                        <span className={`theme-dot theme-dot--${themeOption}`} />
                        <span>{THEME_LABELS[themeOption]}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <PublicLanguageSwitch />
              <button
                type="button"
                className="um-header-btn um-header-btn--logout"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
              >
                <LogoutOutlinedIcon fontSize="inherit" />
                {t("generic.logout", { defaultValue: "Logout" })}
              </button>
            </div>
          </div>
        </div>
      </header>
      <div className="um-shell">
        <section className="um-hero">
          <div className="um-profile">
            <div className={`um-avatar ${user?.id && !avatarFailed ? "has-image" : "is-fallback"}`}>
              {user?.id && !avatarFailed ? (
                <img
                  src={buildEntityFileUrl(user.id, ApiConstants.EntityTypes.User)}
                  alt={user.userName || displayName}
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <span>{(displayName || "U").slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div className="um-profile-copy">
              <h1>{displayName}</h1>
              <p>{user?.email || "-"}</p>
            </div>
          </div>

          <div className="um-stats">
            <div className="um-stat-card">
              <span className="um-stat-icon">
                <HotelOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{summary.reservations}</strong>
              <span>{t("bookingMonitor.reservations")}</span>
            </div>
            <div className="um-stat-card">
              <span className="um-stat-icon">
                <EventNoteOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{summary.bookingRequests}</strong>
              <span>{t("bookingMonitor.requests")}</span>
            </div>
            <div className="um-stat-card">
              <span className="um-stat-icon">
                <TimelineOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{summary.activeStays}</strong>
              <span>{t("bookingMonitor.activeStays")}</span>
            </div>
            <div className="um-stat-card">
              <span className="um-stat-icon">
                <AccountBalanceWalletOutlinedIcon fontSize="inherit" />
              </span>
              <strong>{currencyFormatter.format(summary.totalSpent)}</strong>
              <span>{t("bookingMonitor.totalValue")}</span>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="um-empty">{t("bookingMonitor.loading")}</div>
        ) : error ? (
          <div className="um-empty">{error}</div>
        ) : (
          <div className="um-grid">
            <section className="um-panel">
              <div className="um-panel-head">
                <h2>{t("bookingMonitor.pendingCheckInReservations")}</h2>
                <span>{t("bookingMonitor.items", { count: awaitingCheckInReservations.length })}</span>
              </div>
              <div className="um-activity-list">
                {awaitingCheckInReservations.length === 0 ? (
                  <div className="um-empty um-empty--inline">{t("bookingMonitor.noActivity")}</div>
                ) : (
                  awaitingCheckInReservations.map((item) => (
                    <div key={item.id} className="um-activity-item">
                      <div className="um-activity-dot" />
                      <div className="um-activity-copy">
                        <strong>{item.hotelName || t("bookingMonitor.hotelRef", { id: item.hotelId })}</strong>
                        <p>
                          {item.roomNumber
                            ? t("reservationDesk.roomNumber", { roomNumber: item.roomNumber })
                            : t("bookingMonitor.reservations")}
                        </p>
                        <span>
                          {formatDate(item.checkInDate)} - {formatDate(item.checkOutDate)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="um-panel um-panel--requests">
              <div className="um-panel-head">
                <h2>{t("bookingMonitor.requests")}</h2>
                <div className="um-panel-head-actions">
                  {hasMoreRequests ? (
                    <button
                      type="button"
                      className="um-toggle-btn"
                      onClick={() => setShowAllRequests((current) => !current)}
                    >
                      {showAllRequests ? t("bookingMonitor.showLatestRequests") : t("bookingMonitor.showAll")}
                    </button>
                  ) : null}
                  <span>{bookingRequests.length}</span>
                </div>
              </div>
              <div className="um-card-list">
                {bookingRequests.length === 0 ? (
                  <div className="um-empty um-empty--inline">{t("bookingMonitor.noRequests")}</div>
                ) : (
                  visibleBookingRequests.map((item) => (
                    <article
                      key={item.id}
                      className="um-card"
                    >
                      <div className="um-card-top">
                        <div>
                          <strong>{item.hotelName || t("bookingMonitor.hotelRef", { id: item.hotelId })}</strong>
                          <span>
                            {item.roomNumber
                              ? t("reservationDesk.roomNumber", { roomNumber: item.roomNumber })
                              : item.roomTypeName || t("bookingMonitor.roomTypeOnRequest")}
                          </span>
                        </div>
                        <span className={`um-status status-${item.status}`}>{bookingRequestStatusLabel(item.status)}</span>
                      </div>
                      <div className="um-card-grid">
                        <div>
                          <label>{t("reservationDesk.checkIn")}</label>
                          <span>{formatDate(item.checkInDate)}</span>
                        </div>
                        <div>
                          <label>{t("reservationDesk.checkOut")}</label>
                          <span>{formatDate(item.checkOutDate)}</span>
                        </div>
                        <div>
                          <label>{t("bookingMonitor.created")}</label>
                          <span>{formatDateTime(item.created)}</span>
                        </div>
                        <div>
                          <label>{t("bookingMonitor.request")}</label>
                          <span>{item.specialRequests || "-"}</span>
                        </div>
                        {item.status === 1 ? (
                          <div className="um-card-action-row">
                            <button
                              type="button"
                              className="um-card-cancel-btn"
                              onClick={() => openCancelRequestModal(item)}
                              disabled={cancellingRequestId === item.id}
                            >
                              {cancellingRequestId === item.id
                                ? t("bookingMonitor.cancellingRequest", { defaultValue: "Cancelling..." })
                                : t("generic.cancel")}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))
                )}
              </div>
            </section>
          </div>
        )}

        {cancelRequestItem ? (
          <GeneralModal
            handleClose={closeCancelRequestModal}
            headerName={t("generic.cancel")}
            width="520px"
            radius="18px"
            hideFooter
          >
            <div className="um-cancel-request-modal">
              <p>{t("bookingMonitor.confirmCancelRequest", { defaultValue: "A doni te anuloni rezervimin?" })}</p>
              <div className="um-cancel-request-actions">
                <button
                  type="button"
                  className="um-login-secondary"
                  onClick={closeCancelRequestModal}
                  disabled={cancellingRequestId === cancelRequestItem.id}
                >
                  {t("generic.no", { defaultValue: "Jo" })}
                </button>
                <button
                  type="button"
                  className="um-card-cancel-btn"
                  onClick={submitCancelRequest}
                  disabled={cancellingRequestId === cancelRequestItem.id}
                >
                  {cancellingRequestId === cancelRequestItem.id
                    ? t("bookingMonitor.cancellingRequest", { defaultValue: "Cancelling..." })
                    : t("generic.yes", { defaultValue: "Po" })}
                </button>
              </div>
            </div>
          </GeneralModal>
        ) : null}

        {isProfileModalOpen ? (
          <GeneralModal
            handleClose={() => setIsProfileModalOpen(false)}
            headerName={t("bookingMonitor.profile")}
            width="980px"
            radius="20px"
            hideFooter
          >
            <div className={`um-profile-modal bcShell bcTheme-${theme}`}>
              <div className="um-card um-card--soft">
                <div className="um-panel-head">
                  <h2>{t("bookingMonitor.profile")}</h2>
                  <span>{t("bookingMonitor.personalInfo")}</span>
                </div>
                <div className="um-card-grid">
                  <div>
                    <label>{t("bookingMonitor.fullName")}</label>
                    <span>{displayName}</span>
                  </div>
                  <div>
                    <label>{t("generic.email")}</label>
                    <span>{user?.email || "-"}</span>
                  </div>
                  <div>
                    <label>{t("bookingMonitor.phone")}</label>
                    <span>{user?.phone || "-"}</span>
                  </div>
                  <div>
                    <label>{t("bookingMonitor.timezone")}</label>
                    <span>{monitorUser?.companyTimeZone || "Europe/Tirane"}</span>
                  </div>
                </div>
              </div>

              <div className="um-settings-grid">
                <form className="um-settings-form" onSubmit={handleProfileSave}>
                  <div className="um-panel-head um-panel-head--compact">
                    <h3>{t("bookingMonitor.editProfile")}</h3>
                  </div>
                  <label htmlFor="um-profile-phone">{t("bookingMonitor.phone")}</label>
                  <input
                    id="um-profile-phone"
                    type="text"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    placeholder={t("bookingMonitor.phone")}
                  />
                  <label htmlFor="um-profile-current-password">{t("bookingMonitor.currentPassword")}</label>
                  <input
                    id="um-profile-current-password"
                    type="password"
                    value={profileCurrentPassword}
                    onChange={(e) => setProfileCurrentPassword(e.target.value)}
                    placeholder={t("bookingMonitor.currentPassword")}
                  />
                  {profileError ? <div className="um-empty um-empty--inline">{profileError}</div> : null}
                  {profileMessage ? <div className="um-success">{profileMessage}</div> : null}
                  <button type="submit" className="um-login-btn" disabled={profileSaving}>
                    {profileSaving ? t("generic.saving") : t("generic.save")}
                  </button>
                </form>

                <form className="um-settings-form" onSubmit={handlePasswordSave}>
                  <div className="um-panel-head um-panel-head--compact">
                    <h3>{t("bookingMonitor.changePassword")}</h3>
                  </div>
                  <label htmlFor="um-password-current">{t("bookingMonitor.currentPassword")}</label>
                  <input
                    id="um-password-current"
                    type="password"
                    value={passwordCurrent}
                    onChange={(e) => setPasswordCurrent(e.target.value)}
                    placeholder={t("bookingMonitor.currentPassword")}
                  />
                  <label htmlFor="um-password-next">{t("generic.newPassword")}</label>
                  <input
                    id="um-password-next"
                    type="password"
                    value={passwordNext}
                    onChange={(e) => setPasswordNext(e.target.value)}
                    placeholder={t("generic.newPassword")}
                  />
                  <label htmlFor="um-password-confirm">{t("generic.confirmPassword")}</label>
                  <input
                    id="um-password-confirm"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder={t("generic.confirmPassword")}
                  />
                  {passwordError ? <div className="um-empty um-empty--inline">{passwordError}</div> : null}
                  {passwordMessage ? <div className="um-success">{passwordMessage}</div> : null}
                  <button type="submit" className="um-login-btn" disabled={passwordSaving}>
                    {passwordSaving ? t("generic.saving") : t("bookingMonitor.changePassword")}
                  </button>
                </form>
              </div>

            </div>
          </GeneralModal>
        ) : null}
      </div>
    </div>
  );
};

export default PublicUserMonitor;
